import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, Send, Eye, ChevronDown, ChevronUp } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import {
  QuestionnaireEditor,
  QuestionnaireReadOnly,
} from "@/components/marketplace/QuestionnaireEditor";

/**
 * Combined questionnaire panel for the project detail page.
 *
 *  - Author view: edit prompts + review all readers' submitted answers.
 *  - Accepted-reader view: read prompts + save-draft / submit answers.
 *  - Anyone else: renders nothing (parent already gates on feedback_style
 *    and access; extra guard-rail here is intentional).
 */
export function QuestionnairePanel({ job, isAuthor, isAcceptedReader, onJobUpdated }) {
  const style = job.feedback_style;
  const enabled = style === "questionnaire" || style === "both";
  if (!enabled) return null;
  if (!isAuthor && !isAcceptedReader) return null;

  return isAuthor ? (
    <AuthorPanel job={job} onJobUpdated={onJobUpdated} />
  ) : (
    <ReaderPanel job={job} />
  );
}

function AuthorPanel({ job, onJobUpdated }) {
  const [items, setItems] = useState(job.questionnaire || []);
  const [saving, setSaving] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRows, setReviewRows] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);

  // Keep local list in sync when the parent refetches the job.
  useEffect(() => { setItems(job.questionnaire || []); }, [job.questionnaire]);

  const dirty = useMemo(() => {
    const a = JSON.stringify((items || []).map((q) => ({ id: q.id, prompt: q.prompt, order: q.order })));
    const b = JSON.stringify((job.questionnaire || []).map((q) => ({ id: q.id, prompt: q.prompt, order: q.order })));
    return a !== b;
  }, [items, job.questionnaire]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/jobs/${job.id}`, { questionnaire: items });
      onJobUpdated?.(data);
      toast.success("Questionnaire saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const loadReview = useCallback(async () => {
    setReviewLoading(true);
    try {
      const { data } = await api.get(`/jobs/${job.id}/questionnaire/answers`);
      setReviewRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReviewLoading(false);
    }
  }, [job.id]);

  useEffect(() => {
    if (reviewOpen && reviewRows === null) loadReview();
  }, [reviewOpen, reviewRows, loadReview]);

  return (
    <section className="space-y-3" data-testid="questionnaire-author-panel">
      <QuestionnaireEditor
        value={items}
        onChange={setItems}
        disabled={saving}
        testidPrefix="job-detail-questionnaire"
      />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setReviewOpen((v) => !v)}
          className="btn-ghost border inline-flex items-center gap-1 text-sm"
          data-testid="questionnaire-review-toggle"
        >
          <Eye size={14} />
          {reviewOpen ? "Hide reader responses" : "Review reader responses"}
          {reviewOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          className="btn-primary inline-flex items-center gap-1"
          data-testid="questionnaire-save"
        >
          <Save size={14} /> {saving ? "Saving…" : "Save questionnaire"}
        </button>
      </div>

      {reviewOpen && (
        <div className="rounded-2xl border border-border/70 bg-card p-4" data-testid="questionnaire-review">
          {reviewLoading && <div className="text-sm text-muted-foreground">Loading responses…</div>}
          {!reviewLoading && (!reviewRows || reviewRows.length === 0) && (
            <div className="text-sm text-muted-foreground italic">No responses yet.</div>
          )}
          {!reviewLoading && reviewRows && reviewRows.length > 0 && (
            <div className="space-y-4">
              {reviewRows.map((row) => {
                const byQid = Object.fromEntries((row.answers || []).map((a) => [a.question_id, a]));
                const submitted = !!row.submitted_at;
                return (
                  <div key={row.id} className="rounded-lg border border-border/60 p-3" data-testid={`questionnaire-response-${row.reader_id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                      <div className="text-sm font-semibold">
                        {row.reader?.display_name || row.reader?.email || row.reader?.id}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.14em]">
                        <span className={`px-2 py-0.5 rounded-full ${
                          submitted
                            ? "bg-[hsl(var(--sb-forest))] text-white"
                            : "bg-muted text-muted-foreground"
                        }`}>
                          {submitted ? "Submitted" : "Draft"}
                        </span>
                        {row.updated_at && (
                          <span className="ml-2 text-muted-foreground">
                            {new Date(row.submitted_at || row.updated_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <QuestionnaireReadOnly
                      questionnaire={job.questionnaire || []}
                      answersByQid={byQid}
                      emptyLabel="This project has no questions."
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function ReaderPanel({ job }) {
  const [existing, setExisting] = useState(null); // null until load
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Local map: question_id -> response text
  const [byQid, setByQid] = useState({});

  const sortedQ = useMemo(
    () => (job.questionnaire || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)),
    [job.questionnaire]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/jobs/${job.id}/questionnaire/answers`);
      const mine = Array.isArray(data) && data.length > 0 ? data[0] : null;
      setExisting(mine);
      const m = {};
      (mine?.answers || []).forEach((a) => { m[a.question_id] = a.response || ""; });
      setByQid(m);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [job.id]);

  useEffect(() => { load(); }, [load]);

  const payloadAnswers = useMemo(
    () => sortedQ.map((q) => ({ question_id: q.id, response: byQid[q.id] || "" })),
    [sortedQ, byQid]
  );

  const send = async (asSubmit) => {
    const setter = asSubmit ? setSubmitting : setSaving;
    setter(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/questionnaire/answers`, {
        answers: payloadAnswers,
        submit: asSubmit,
      });
      setExisting(data);
      toast.success(asSubmit ? "Answers submitted" : "Draft saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setter(false);
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading questionnaire…</div>;
  if (sortedQ.length === 0) {
    return (
      <div className="text-sm text-muted-foreground italic" data-testid="questionnaire-reader-empty">
        The author hasn&rsquo;t added any questions yet.
      </div>
    );
  }

  return (
    <section className="rounded-2xl border border-border/70 bg-card p-4 space-y-3" data-testid="questionnaire-reader-panel">
      <div>
        <div className="text-sm font-semibold">Feedback Questionnaire</div>
        <div className="text-xs text-muted-foreground mt-0.5">
          Answer the author&rsquo;s questions. You can save a draft and come back to it, then submit when you&rsquo;re done.
        </div>
      </div>
      <ol className="space-y-3 list-decimal pl-5">
        {sortedQ.map((q) => (
          <li key={q.id} data-testid={`questionnaire-reader-item-${q.id}`}>
            <div className="text-sm font-medium">{q.prompt}</div>
            <textarea
              rows={3}
              maxLength={5000}
              value={byQid[q.id] || ""}
              onChange={(e) => setByQid((m) => ({ ...m, [q.id]: e.target.value }))}
              disabled={saving || submitting}
              className="sb-input mt-1 resize-none text-sm"
              placeholder="Your answer…"
              data-testid={`questionnaire-reader-answer-${q.id}`}
            />
          </li>
        ))}
      </ol>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] text-muted-foreground">
          {existing?.submitted_at
            ? `Submitted ${new Date(existing.submitted_at).toLocaleString()} — you can update and re-submit anytime.`
            : existing?.updated_at
              ? `Draft saved ${new Date(existing.updated_at).toLocaleString()}`
              : "Not yet saved."}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => send(false)}
            disabled={saving || submitting}
            className="btn-ghost border text-sm"
            data-testid="questionnaire-reader-save"
          >
            {saving ? "Saving…" : "Save draft"}
          </button>
          <button
            type="button"
            onClick={() => send(true)}
            disabled={saving || submitting}
            className="btn-primary inline-flex items-center gap-1"
            data-testid="questionnaire-reader-submit"
          >
            <Send size={14} /> {submitting ? "Submitting…" : "Submit answers"}
          </button>
        </div>
      </div>
    </section>
  );
}

export default QuestionnairePanel;
